import React, { useState } from 'react';
import { ChevronDownIcon } from 'lucide-react';
import type { PolicySection } from '../../types/policy';
interface PolicyContentsProps {
  sections: PolicySection[];
  activeId: string;
  /** Accessible name for the section index, e.g. "Privacy Policy sections". */
  label?: string;
}
const focusRing = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-600 focus-visible:ring-offset-2 focus-visible:ring-offset-ivory';
export function PolicyContents({
  sections,
  activeId,
  label = 'Privacy Policy sections'
}: PolicyContentsProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const activeSection = sections.find(section => section.id === activeId);
  return <>
      {/* Mobile: collapsible contents */}
      <div className="lg:hidden sticky top-[73px] z-30 -mx-6 px-6 py-3 bg-ivory/95 backdrop-blur-sm border-b border-charcoal/10" data-id="element-259" data-mp="3o9jjuc5nzwl1">
        <button type="button" onClick={() => setMobileOpen(open => !open)} aria-expanded={mobileOpen} aria-controls="policy-contents-mobile" className={`w-full flex items-center justify-between gap-3 rounded-xl border border-purple-200 bg-white px-4 py-3 text-left ${focusRing}`} data-id="element-260" data-mp="3jb87nurwd9zn">
          <span className="min-w-0" data-id="element-261" data-mp="22w8cfkwedikd">
            <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-purple-700" data-id="element-262" data-mp="27u9r7tmsivq1">
              Contents
            </span>
            <span className="block truncate text-sm text-charcoal/80" data-id="element-263" data-mp="1a8cgiienjuon">
              {activeSection ? `${activeSection.number}. ${activeSection.title}` : 'Jump to a section'}
            </span>
          </span>
          <ChevronDownIcon aria-hidden="true" className={`w-5 h-5 shrink-0 text-purple-700 transition-transform duration-200 ease-out ${mobileOpen ? 'rotate-180' : ''}`} data-id="element-264" data-mp="1ovfc2jjezt9c" />
        </button>

        {mobileOpen && <nav id="policy-contents-mobile" aria-label={label} className="mt-2 max-h-[60vh] overflow-y-auto rounded-xl border border-purple-200 bg-white py-2" data-id="element-265" data-mp="wda3lbbe152c">
            <ol className="text-sm" data-id="element-266" data-mp="jdrcxta79mfs">
              {sections.map(section => {
            const isActive = section.id === activeId;
            return <li key={section.id} data-id="element-267" data-mp="t5x4x30gc3uz">
                    <a href={`#${section.id}`} onClick={() => setMobileOpen(false)} aria-current={isActive ? 'true' : undefined} className={`flex gap-2 px-4 py-2.5 ${focusRing} ${isActive ? 'text-purple-800 font-semibold bg-purple-50' : 'text-charcoal/75 hover:text-purple-800 hover:bg-purple-50'}`} data-id="element-268" data-mp="5fuip3j7xs4a">
                      <span className="tabular-nums text-charcoal/40" data-id="element-269" data-mp="1lucfndcjw00i">
                        {section.number}.
                      </span>
                      <span data-id="element-270" data-mp="1tc0matol99gm">{section.title}</span>
                    </a>
                  </li>;
          })}
            </ol>
          </nav>}
      </div>

      {/* Desktop: sticky index */}
      <nav aria-label={label} className="hidden lg:block sticky top-32 max-h-[calc(100vh-11rem)] overflow-y-auto pr-4" data-id="element-271" data-mp="1keqddez8twev">
        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-purple-700 mb-4" data-id="element-272" data-mp="2c3l2ew7jbpd5">
          On this page
        </p>
        <ol className="space-y-1 border-l border-charcoal/10" data-id="element-273" data-mp="2h1v0bekn942g">
          {sections.map(section => {
          const isActive = section.id === activeId;
          return <li key={section.id} data-id="element-274" data-mp="27cbdjxtt8q2b">
                <a href={`#${section.id}`} aria-current={isActive ? 'true' : undefined} className={`block -ml-px border-l-2 pl-4 py-1.5 text-sm leading-snug transition-colors duration-200 ease-out ${focusRing} ${isActive ? 'border-purple-600 text-purple-800 font-semibold' : 'border-transparent text-charcoal/65 hover:text-purple-800 hover:border-purple-300'}`} data-id="element-275" data-mp="27bjq5aad9yn4">
                  <span className="tabular-nums text-charcoal/40 mr-1.5" data-id="element-276" data-mp="128zkjxbqvuuq">
                    {section.number}.
                  </span>
                  {section.title}
                </a>
              </li>;
        })}
        </ol>
      </nav>
    </>;
}